/**
 * AudioUpload Component
 *
 * Allows users to upload audio files for transcription.
 * - Dictation mode: Returns raw transcript text
 * - Conversation mode: Returns diarized conversation with speaker labels
 * Shows confirmation dialog asking if user wants to save the recording.
 *
 * Reconnect: If the SSE connection is dropped (Azure 240s ingress timeout),
 * the component automatically reconnects using the job_id received at stream start.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const MP4_MIME_TYPES = new Set([
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4a-latm',
  'audio/m4b',
  'audio/x-m4b',
  'audio/m4p',
  'audio/x-m4p',
  'audio/m4r',
  'audio/x-m4r',
  'audio/x-mp4',
  'video/mp4',
  'video/x-m4v',
  'application/mp4',
]);
const MP4_EXTENSIONS = ['.mp4', '.m4a', '.m4b', '.m4p', '.m4r', '.m4v'];
const ACCEPTED_AUDIO = '.wav,.wave,.mp3,.webm,.ogg,.oga,.opus,.flac';

const isMp4Family = (file: File) => {
  const fileName = file.name.toLowerCase();
  return MP4_MIME_TYPES.has(file.type.toLowerCase()) || MP4_EXTENSIONS.some((ext) => fileName.endsWith(ext));
};

// Browsers commonly report audio-only .webm files as video/webm.
const isAudioFile = (file: File) => file.type.startsWith('audio/') || file.type === 'video/webm';

type PersistenceStatus = 'saved' | 'partial' | 'failed';

interface TranscriptResult {
  status: 'success' | 'error';
  text: string;
  language?: string;
  duration?: number;
  processing_time_ms?: number;
  audio_id?: string;
  save_requested?: boolean;
  persistence_status?: PersistenceStatus;
  transcript_saved?: boolean;
  audio_saved?: boolean;
  file?: File;
}

interface DiarizedSegment {
  speaker: 'doctor' | 'patient' | string;
  text: string;
  start: number;
  end: number;
}

interface DiarizedResult {
  status: 'success' | 'error';
  segments: DiarizedSegment[];
  num_speakers: number;
  language?: string;
  duration?: number;
  ai_reviewed: boolean;
  audio_id?: string;
  save_requested?: boolean;
  persistence_status?: PersistenceStatus;
  transcript_saved?: boolean;
  audio_saved?: boolean;
  raw_transcript?: string[];
  file?: File;
}

/** Union of the payload shapes carried by the upload SSE events. */
interface SseEventData {
  job_id?: string;
  percent?: number;
  message?: string;
  error?: string;
  segments?: DiarizedSegment[];
  duration?: number;
  save_requested?: boolean;
  persistence_status?: PersistenceStatus;
  transcript_saved?: boolean;
  audio_saved?: boolean;
}

interface AudioUploadProps {
  /** Mode determines which endpoint to call */
  mode: 'dictation' | 'conversation';
  /** Language code for transcription */
  language: string;
  /** Disable upload button */
  disabled?: boolean;
  /** Enable rule-based normalization (spoken punctuation → symbols) */
  enableRuleBasedNormalization?: boolean;
  /**
   * Ensure a persisted session exists and return its id (and resolved patient).
   * Called only when the user opts to save; the returned session_id is forwarded
   * to the upload so the transcript persists under the URL's session.
   */
  ensureSession?: () => Promise<{
    session_id: string;
    patient?: { name?: string; dbId?: number | string; fhirPatientId?: string };
  }>;
  /** Callback when dictation mode upload completes */
  onTranscriptComplete?: (result: TranscriptResult) => void;
  /** Callback when conversation mode upload completes */
  onDiarizeComplete?: (result: DiarizedResult) => void;
  /** Callback when upload activity changes */
  onUploadActiveChange?: (active: boolean) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 3000;

/**
 * The server emits a progress event every 2s and an SSE ping every 5s, so a
 * gap this long means the connection is dead rather than quiet. Needed because
 * a dropped stream usually stalls silently — reader.read() then never settles
 * and the upload would otherwise sit at its last percentage forever.
 */
const STALL_TIMEOUT_MS = 30000;

/**
 * How long to wait for the status endpoint's response headers. Only guards the
 * header wait — the timer is cleared once fetch() resolves, so the long-lived
 * SSE body is never aborted.
 */
const STATUS_HEADER_TIMEOUT_MS = 15000;

const STALLED = Symbol('stalled');

/**
 * The job itself failed server-side (an `error` SSE event). Distinct from a
 * transport failure because it must never be retried — the job is terminal,
 * and its message is the one worth showing the user.
 */
class UploadFailedError extends Error {}

/** Fetch that gives up if response headers never arrive, without aborting the body stream. */
const fetchWithHeaderTimeout = async (url: string): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STATUS_HEADER_TIMEOUT_MS);

  try {
    return await fetch(url, { credentials: 'include', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

type StreamReader = ReadableStreamDefaultReader<Uint8Array>;

/** Read the next chunk, resolving to STALLED if nothing arrives before the watchdog fires. */
const readWithStallTimeout = async (
  reader: StreamReader
): Promise<ReadableStreamReadResult<Uint8Array> | typeof STALLED> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stalled = new Promise<typeof STALLED>((resolve) => {
    timer = setTimeout(() => resolve(STALLED), STALL_TIMEOUT_MS);
  });

  try {
    return await Promise.race([reader.read(), stalled]);
  } finally {
    clearTimeout(timer);
  }
};

export function AudioUpload({
  mode,
  language,
  disabled = false,
  enableRuleBasedNormalization = false,
  ensureSession,
  onTranscriptComplete,
  onDiarizeComplete,
  onUploadActiveChange,
}: AudioUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jobIdRef = useRef<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    onUploadActiveChange?.(isUploading);
  }, [isUploading, onUploadActiveChange]);

  useEffect(() => {
    return () => {
      onUploadActiveChange?.(false);
    };
  }, [onUploadActiveChange]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input for re-uploads
    e.target.value = '';

    if (isMp4Family(file)) {
      toast({
        title: 'Óstutt skráarsnið',
        description: 'MP4/M4A hljóðskrár eru ekki studdar. Veldu wav, mp3, webm, ogg eða flac.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file type
    if (!isAudioFile(file)) {
      toast({
        title: 'Ógild skrá',
        description: 'Vinsamlegast veldu hljóðskrá (wav, mp3, webm, ogg, flac)',
        variant: 'destructive',
      });
      return;
    }

    // Check file size (500MB limit)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: 'Skrá of stór',
        description: 'Hámarksstærð er 500MB',
        variant: 'destructive',
      });
      return;
    }

    // Store file and show confirmation dialog
    setPendingFile(file);
    setShowConfirmDialog(true);
  };

  const handleUpload = async (saveAudio: boolean) => {
    setShowConfirmDialog(false);

    if (!pendingFile) return;

    const file = pendingFile;
    setPendingFile(null);
    jobIdRef.current = null;
    setIsUploading(true);
    setUploadProgress(0);
    setUploadMessage('Hleð upp...');

    try {
      // When saving, ensure a persisted session first so the upload attaches the
      // transcript to the URL's session_id (server resolves identity from the cookie).
      let session: { session_id: string } | undefined;
      if (saveAudio && ensureSession) {
        session = await ensureSession();
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);
      formData.append('save_audio', saveAudio ? 'true' : 'false');
      if (session) {
        formData.append('session_id', session.session_id);
      }
      if (mode === 'dictation') {
        formData.append('enable_rule_based_normalization', enableRuleBasedNormalization ? 'true' : 'false');
      }

      // Use streaming endpoint for both modes
      const endpoint =
        mode === 'conversation' ? '/api/audio/upload/diarize-stream' : '/api/audio/upload/transcribe-stream';
      await handleStreamingUpload(file, formData, endpoint);
    } catch (error: unknown) {
      console.error('[AudioUpload] Error:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að vinna úr hljóðskrá',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadMessage('');
    }
  };

  /** Parse SSE stream and dispatch events. Returns true if a complete event was received. */
  const consumeSseStream = async (response: Response, file: File | undefined): Promise<boolean> => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const result = await readWithStallTimeout(reader);

      if (result === STALLED) {
        console.warn('[AudioUpload] SSE stream went silent — treating as dropped');
        await reader.cancel().catch(() => undefined);
        return false;
      }

      const { done, value } = result;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, '\n');

      while (buffer.includes('\n\n')) {
        const eventEnd = buffer.indexOf('\n\n');
        const eventBlock = buffer.substring(0, eventEnd);
        buffer = buffer.substring(eventEnd + 2);

        if (!eventBlock.trim()) continue;

        const lines = eventBlock.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.slice(5).trim();
          }
          // SSE comments (ping) are ignored
        }

        if (!eventData) continue;

        let data: SseEventData;
        try {
          data = JSON.parse(eventData);
        } catch {
          console.error('[AudioUpload] JSON parse error:', eventData.substring(0, 100));
          continue;
        }

        if (eventType === 'job_id') {
          jobIdRef.current = data.job_id ?? null;
        } else if (eventType === 'progress') {
          setUploadProgress(data.percent ?? 0);
          setUploadMessage(data.message || 'Vinnur...');
        } else if (eventType === 'complete') {
          setUploadProgress(100);
          setUploadMessage('Lokið!');
          const savedMsg = data.persistence_status === 'saved' || data.audio_saved ? ' og vistað' : '';
          const persistenceIncomplete =
            data.save_requested === true &&
            (data.persistence_status === 'partial' || data.persistence_status === 'failed');
          if (data.segments) {
            onDiarizeComplete?.({ ...data, file } as DiarizedResult);
            if (!persistenceIncomplete) {
              toast({
                title: 'Hljóðskrá afgreidd',
                description: `${data.segments.length} skilaboð greind${savedMsg}`,
              });
            }
          } else {
            onTranscriptComplete?.({ ...data, file } as TranscriptResult);
            if (!persistenceIncomplete) {
              toast({
                title: 'Hljóðskrá afgreidd',
                description: data.duration
                  ? `${Math.round(data.duration)}s hljóð umritað${savedMsg}`
                  : `Texti umritaður${savedMsg}`,
              });
            }
          }
          if (persistenceIncomplete) {
            toast({
              title: 'Hljóðvinnslu lokið — ekki tókst að vista allt',
              description: 'Geymdu textann sem birtist áður en þú ferð af síðunni.',
              variant: 'destructive',
            });
          }
          return true;
        } else if (eventType === 'error') {
          throw new UploadFailedError(data.error || 'Villa við hljóðgreiningu');
        }
      }
    }

    return false; // stream ended without complete event
  };

  /** Reconnect to an in-progress job using the status stream endpoint. */
  const reconnectToJob = async (jobId: string, file: File | undefined): Promise<void> => {
    for (let attempt = 1; attempt <= MAX_RECONNECT_ATTEMPTS; attempt++) {
      setUploadMessage(`Tengist aftur... (${attempt}/${MAX_RECONNECT_ATTEMPTS})`);

      await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));

      try {
        const response = await fetchWithHeaderTimeout(`/api/audio/upload/status/${jobId}/stream`);

        if (!response.ok) {
          console.warn(`[AudioUpload] Reconnect HTTP ${response.status}`);
          continue;
        }

        const completed = await consumeSseStream(response, file);
        if (completed) return;
        // Stream ended again without complete — retry
      } catch (err) {
        // A failed job is terminal: retrying just masks the real reason.
        if (err instanceof UploadFailedError) throw err;
        console.warn(`[AudioUpload] Reconnect attempt ${attempt} failed:`, err);
      }
    }

    throw new Error('Ekki tókst að tengjast aftur. Vinsamlegast reyndu aftur.');
  };

  const handleStreamingUpload = async (file: File, formData: FormData, endpoint: string) => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      let completed: boolean;
      try {
        completed = await consumeSseStream(response, file);
      } catch (streamError: unknown) {
        // A transport failure (reset connection, aborted read) is recoverable —
        // the pipeline keeps running server-side, so pick it up via the job_id.
        // Only a terminal job failure should surface directly.
        if (streamError instanceof UploadFailedError || !jobIdRef.current) throw streamError;
        console.warn('[AudioUpload] Stream read failed, reconnecting:', streamError);
        completed = false;
      }

      // If the stream ended without a complete event (dropped or silently
      // stalled connection), reconnect using the job_id.
      if (!completed) {
        if (!jobIdRef.current) {
          throw new Error('Tengingin slitnaði áður en vinnsla byrjaði. Vinsamlegast reyndu aftur.');
        }
        await reconnectToJob(jobIdRef.current, file);
      }
    } catch (error: unknown) {
      console.error('[AudioUpload] Stream error:', error);
      toast({
        title: 'Villa',
        description: error instanceof Error ? error.message : 'Ekki tókst að vinna úr hljóðskrá',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadMessage('');
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setPendingFile(null);
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept={ACCEPTED_AUDIO} onChange={handleFileChange} className="hidden" />
      {isUploading ? (
        <div className="flex min-w-[180px] items-center gap-2">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center justify-between text-muted-foreground text-xs">
              <span className="truncate">{uploadMessage || 'Vinnur...'}</span>
              <span className="ml-2 font-medium">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-1.5" />
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 hover:text-primary"
          onClick={handleClick}
          disabled={disabled}
          title="Hlaða upp hljóðskrá"
        >
          <Upload className="mr-1 h-4 w-4" />
          Hlaða upp
        </Button>
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vista upptöku?</AlertDialogTitle>
            <AlertDialogDescription>
              Viltu vista þessa hljóðskrá í gagnagrunni? Ef þú velur "Nei" verður hljóðskráin aðeins umrituð en ekki
              vistuð.
              {pendingFile && (
                <span className="mt-2 block font-medium text-sm">
                  Skrá: {pendingFile.name} ({(pendingFile.size / (1024 * 1024)).toFixed(1)} MB)
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Hætta við</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleUpload(false)}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
            >
              Nei, bara umrita
            </AlertDialogAction>
            <AlertDialogAction onClick={() => handleUpload(true)}>Já, vista</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
